import { Component, OnInit } from '@angular/core';
import { HttpErrorResponse } from '@angular/common/http';
import { BsModalRef, BsModalService } from 'ngx-bootstrap/modal';

import { XrefApiKeyManageModalComponent } from 'src/app/components/xref-api-key-manage-modal/xref-api-key-manage-modal.component';
import { XrefApiWebhookModalComponent } from 'src/app/components/xref-api-webhook-modal/xref-api-webhook-modal.component';
import { AlertBannerService, AlertType } from 'src/app/lib/alert-banner';
import { WarningModalService } from 'src/app/services/warning-modal/warning-modal.service';
import {
  XrefApiKeyMasked,
  XrefApiService,
  XrefApiWebhookInfo,
} from 'src/app/services/xref-api/xref-api.service';

@Component({
  selector: 'app-xref-api',
  templateUrl: './xref-api.component.pug',
  styleUrls: ['./xref-api.component.scss'],
  standalone: false,
})
export class XrefApiComponent implements OnInit {
  isLoading = true;
  isGenerating = false;
  isSavingWebhook = false;

  apiKey: XrefApiKeyMasked | null = null;
  webhook: XrefApiWebhookInfo | null = null;

  private keyModalRef: BsModalRef | null = null;
  private webhookModalRef: BsModalRef | null = null;

  constructor(
    private xrefApiService: XrefApiService,
    private alertBannerService: AlertBannerService,
    private warningModalService: WarningModalService,
    private modalService: BsModalService,
  ) {}

  ngOnInit(): void {
    this.loadAll();
  }

  loadAll(): void {
    this.isLoading = true;
    let pending = 2;
    const done = () => {
      pending -= 1;
      if (pending === 0) {
        this.isLoading = false;
      }
    };

    this.xrefApiService.getKey().subscribe({
      next: (res) => {
        this.apiKey = res;
        done();
      },
      error: (err: HttpErrorResponse) => {
        this.handleLoadError(err);
        done();
      },
    });

    this.xrefApiService.getWebhook().subscribe({
      next: (res) => {
        this.webhook = res;
        done();
      },
      error: (err: HttpErrorResponse) => {
        this.handleLoadError(err);
        done();
      },
    });
  }

  get apiKeyButtonLabel(): string {
    return this.apiKey ? $localize`Manage API key` : $localize`Generate API key`;
  }

  get webhookButtonLabel(): string {
    return this.webhook ? $localize`Manage webhook URL` : $localize`Setup webhook URL`;
  }

  showKeyModal(): void {
    const initialState = { apiKey: this.apiKey };
    this.keyModalRef = this.modalService.show(
      XrefApiKeyManageModalComponent,
      Object.assign({}, { class: 'modal-dialog-centered', initialState }),
    );
    const content = this.keyModalRef.content;
    content.generateClick.subscribe(() => this.generateKey());
    content.revokeClick.subscribe(() => this.revokeKey());
  }

  showWebhookModal(): void {
    const initialState = { webhook: this.webhook };
    this.webhookModalRef = this.modalService.show(
      XrefApiWebhookModalComponent,
      Object.assign({}, { class: 'modal-dialog-centered', initialState }),
    );
    const content = this.webhookModalRef.content;
    content.saveClick.subscribe((url: string) => this.saveWebhook(url));
    content.deleteClick.subscribe(() => this.deleteWebhook());
  }

  generateKey(): void {
    if (this.isGenerating) {
      return;
    }
    this.isGenerating = true;
    this.xrefApiService.generateKey().subscribe({
      next: (res) => {
        const content = this.keyModalRef?.content;
        if (content) {
          content.plaintextKey = res.value;
          content.state = 'revealed';
        }
        this.apiKey = {
          value: this.maskKey(res.value),
          created_at: new Date().toISOString(),
          last_used: null,
        };
        this.isGenerating = false;
        this.alertBannerService.append([
          {
            type: AlertType.SUCCESS,
            message: $localize`Success! API key has been generated.`,
          },
        ]);
      },
      error: () => {
        this.isGenerating = false;
        this.alertBannerService.append([
          {
            type: AlertType.DANGER,
            message: $localize`Could not generate the API key. Please try again.`,
          },
        ]);
      },
    });
  }

  revokeKey(): void {
    this.warningModalService
      .confirm(
        $localize`Are you sure you want to revoke this key?`,
        $localize`Revoking this API key will immediately disable access for integrations currently using it. This action cannot be undone.`,
        false,
        $localize`Cancel`,
        $localize`Revoke`,
        true,
        undefined,
        undefined,
        true,
      )
      .subscribe((confirmed: boolean) => {
        if (!confirmed) {
          return;
        }
        this.xrefApiService.revokeKey().subscribe({
          next: () => {
            this.apiKey = null;
            this.keyModalRef?.hide();
            this.alertBannerService.append([
              {
                type: AlertType.SUCCESS,
                message: $localize`Success! API key has been revoked.`,
              },
            ]);
          },
          error: () => {
            this.alertBannerService.append([
              {
                type: AlertType.DANGER,
                message: $localize`Could not revoke the API key. Please try again.`,
              },
            ]);
          },
        });
      });
  }

  saveWebhook(url: string): void {
    if (!url || this.isSavingWebhook) {
      return;
    }
    this.isSavingWebhook = true;
    this.xrefApiService.saveWebhook(url).subscribe({
      next: (res) => {
        this.isSavingWebhook = false;
        this.webhook = { url: res.url, secret: res.secret ?? null };
        const content = this.webhookModalRef?.content;
        if (content) {
          content.webhook = this.webhook;
        }
        this.alertBannerService.append([
          {
            type: AlertType.SUCCESS,
            message: $localize`Success! Webhook URL has been saved.`,
          },
        ]);
      },
      error: () => {
        this.isSavingWebhook = false;
        this.alertBannerService.append([
          {
            type: AlertType.DANGER,
            message: $localize`Could not save the webhook. Please check the URL and try again.`,
          },
        ]);
      },
    });
  }

  deleteWebhook(): void {
    this.xrefApiService.deleteWebhook().subscribe({
      next: () => {
        this.webhook = null;
        this.webhookModalRef?.hide();
        this.alertBannerService.append([
          {
            type: AlertType.SUCCESS,
            message: $localize`Success! Webhook URL has been deleted.`,
          },
        ]);
      },
      error: () => {
        this.alertBannerService.append([
          {
            type: AlertType.DANGER,
            message: $localize`Could not remove the webhook. Please try again.`,
          },
        ]);
      },
    });
  }

  private maskKey(value: string): string {
    if (!value) {
      return '';
    }
    if (value.length <= 4) {
      return '*'.repeat(value.length);
    }
    return '*'.repeat(value.length - 4) + value.slice(-4);
  }

  private handleLoadError(err: HttpErrorResponse): void {
    if (err && err.status === 404) {
      return;
    }
    this.alertBannerService.append([
      {
        type: AlertType.DANGER,
        message: $localize`Could not load Xref API settings. Please try again.`,
      },
    ]);
  }
}
